import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Users, BarChart3, Eye, MousePointer, MapPin, Smartphone, Monitor } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MiniBar({ data, colorMap }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return <p className="text-xs text-slate-400">No data</p>;
  return (
    <div className="space-y-1.5">
      {Object.entries(data).map(([key, val]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-[11px] text-slate-600 w-24 truncate capitalize">{key.replace(/_/g, ' ')}</span>
          <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${colorMap?.[key] || 'bg-primary'}`}
              style={{ width: `${Math.max((val / total) * 100, 4)}%` }} />
          </div>
          <span className="text-[11px] font-semibold text-slate-700 w-8 text-right">{val}</span>
        </div>
      ))}
    </div>
  );
}

function PieChart({ data, colors }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return <p className="text-xs text-slate-400 text-center py-6">No data yet</p>;
  let cumulative = 0;
  const segments = Object.entries(data).filter(([, v]) => v > 0).map(([key, val], i) => {
    const pct = (val / total) * 100;
    const start = cumulative;
    cumulative += pct;
    return { key, val, pct, start, color: colors[i % colors.length] };
  });
  const gradientParts = segments.map(s => `${s.color} ${s.start}% ${s.start + s.pct}%`).join(', ');

  return (
    <div className="flex items-center gap-4">
      <div className="w-28 h-28 rounded-full flex-shrink-0"
        style={{ background: `conic-gradient(${gradientParts})` }} />
      <div className="space-y-1">
        {segments.map(s => (
          <div key={s.key} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-[11px] text-slate-600 capitalize">{s.key.replace(/_/g, ' ')}</span>
            <span className="text-[11px] font-bold">{s.val} ({Math.round(s.pct)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function UserAnalyticsTab() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const t = (en, es) => language === 'es' ? es : en;
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/users/analytics`, { headers }).then(r => setUsers(r.data.users)),
      axios.get(`${API}/admin/analytics/summary`, { headers }).then(r => setSummary(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const filtered = users.filter(u =>
    u.role !== 'superadmin' && (!search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()))
  );

  const chartColors = ['#0B192C', '#D4AF37', '#3b82f6', '#eab308', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>;

  return (
    <div className="space-y-6" data-testid="user-analytics-tab">
      {/* Summary Charts */}
      {summary && (
        <>
          {/* Activity Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="activity-overview">
            {[
              { label: t('Searches', 'Busquedas'), value: summary.activity.total_searches, icon: Search, color: 'text-blue-600 bg-blue-50' },
              { label: t('Views', 'Vistas'), value: summary.activity.total_views, icon: Eye, color: 'text-primary bg-teal-50' },
              { label: t('Contact Clicks', 'Clics Contacto'), value: summary.activity.total_contacts, icon: MousePointer, color: 'text-orange-600 bg-orange-50' },
              { label: t('Map Uses', 'Usos de Mapa'), value: summary.activity.total_map_interactions, icon: MapPin, color: 'text-purple-600 bg-purple-50' },
            ].map((s, i) => (
              <Card key={i}>
                <CardContent className="p-3 flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s.color}`}><s.icon className="w-4 h-4" /></div>
                  <div><div className="font-heading text-lg font-bold">{s.value}</div><div className="text-[11px] text-slate-500 leading-tight">{s.label}</div></div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="analytics-charts">
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm mb-3">{t('User Intent', 'Intencion del Usuario')}</h3>
                <PieChart data={summary.intent} colors={chartColors} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm mb-3">{t('Budget Range', 'Rango de Presupuesto')}</h3>
                <MiniBar data={summary.budget} colorMap={{
                  '0-50k': 'bg-teal-400', '50k-100k': 'bg-teal-500', '100k-250k': 'bg-primary',
                  '250k-500k': 'bg-teal-700', '500k+': 'bg-teal-800'
                }} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm mb-3">{t('Timeline', 'Plazo')}</h3>
                <PieChart data={summary.timeline} colors={['#0B192C', '#D4AF37', '#3b82f6']} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm mb-3">{t('Experience Level', 'Nivel de Experiencia')}</h3>
                <PieChart data={summary.experience} colors={['#0B192C', '#D4AF37']} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm mb-3">{t('Age Distribution', 'Distribucion de Edad')}</h3>
                <MiniBar data={summary.age} colorMap={{
                  '18-24': 'bg-blue-400', '25-34': 'bg-blue-500', '35-44': 'bg-primary',
                  '45-54': 'bg-teal-600', '55-64': 'bg-teal-700', '65+': 'bg-teal-800'
                }} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm mb-3">{t('Property Type Preference', 'Preferencia de Tipo')}</h3>
                <PieChart data={summary.property_type} colors={chartColors} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm mb-3">{t('Device Breakdown', 'Tipo de Dispositivo')}</h3>
                <PieChart data={summary.activity.devices} colors={['#0B192C', '#D4AF37']} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm mb-3">{t('Profile Completion', 'Completitud de Perfil')}</h3>
                <MiniBar data={summary.profile_completion} colorMap={{
                  '0-25%': 'bg-red-400', '26-50%': 'bg-yellow-400', '51-75%': 'bg-blue-400', '76-100%': 'bg-green-500'
                }} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm mb-3">{t('Preferred Cities', 'Ciudades Preferidas')}</h3>
                {Object.keys(summary.city).length > 0
                  ? <MiniBar data={summary.city} />
                  : <p className="text-xs text-slate-400">No data</p>}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Users Table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-base">{t('User Details', 'Detalle de Usuarios')}</h3>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder={t('Search users...', 'Buscar usuarios...')} value={search}
              onChange={e => setSearch(e.target.value)} className="pl-9 h-9 bg-slate-50" data-testid="analytics-search-users" />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-border overflow-x-auto" data-testid="analytics-users-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('Name', 'Nombre')}</TableHead>
                <TableHead>{t('Role', 'Rol')}</TableHead>
                <TableHead>{t('Intent', 'Intencion')}</TableHead>
                <TableHead>{t('Budget', 'Presupuesto')}</TableHead>
                <TableHead>{t('Timeline', 'Plazo')}</TableHead>
                <TableHead>{t('Location', 'Ubicacion')}</TableHead>
                <TableHead>{t('Searches', 'Busquedas')}</TableHead>
                <TableHead>{t('Views', 'Vistas')}</TableHead>
                <TableHead>{t('Contacts', 'Contactos')}</TableHead>
                <TableHead>{t('Saved', 'Guardadas')}</TableHead>
                <TableHead>{t('Map', 'Mapa')}</TableHead>
                <TableHead>{t('Device', 'Dispositivo')}</TableHead>
                <TableHead>{t('Visits', 'Visitas')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(u => {
                const a = u.activity || {};
                const devices = a.devices || {};
                const mainDevice = Object.entries(devices).sort(([,a],[,b]) => b - a)[0]?.[0] || '-';
                return (
                  <TableRow key={u.id} data-testid={`analytics-user-${u.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{u.name}</p>
                        <p className="text-[11px] text-slate-500">{u.email}</p>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="capitalize text-xs">{u.role}</Badge></TableCell>
                    <TableCell><span className="text-xs capitalize">{u.intent?.replace(/_/g, ' ') || '-'}</span></TableCell>
                    <TableCell>
                      <span className="text-xs">
                        {u.budget_min || u.budget_max
                          ? `$${(u.budget_min || 0).toLocaleString()} - $${(u.budget_max || 0).toLocaleString()}`
                          : '-'}
                      </span>
                    </TableCell>
                    <TableCell><span className="text-xs capitalize">{u.timeline?.replace(/_/g, ' ') || '-'}</span></TableCell>
                    <TableCell><span className="text-xs">{[u.city_suburb, u.state_region].filter(Boolean).join(', ') || '-'}</span></TableCell>
                    <TableCell><span className="text-sm font-medium">{a.searches || 0}</span></TableCell>
                    <TableCell><span className="text-sm font-medium">{a.listings_viewed || 0}</span></TableCell>
                    <TableCell><span className="text-sm font-medium">{a.contact_clicks || 0}</span></TableCell>
                    <TableCell><span className="text-sm font-medium">{a.saved_properties || 0}</span></TableCell>
                    <TableCell><span className="text-sm font-medium">{a.map_interactions || 0}</span></TableCell>
                    <TableCell>
                      {mainDevice !== '-' ? (
                        <span className="flex items-center gap-1 text-xs">
                          {mainDevice === 'mobile' ? <Smartphone className="w-3.5 h-3.5" /> : <Monitor className="w-3.5 h-3.5" />}
                          {mainDevice}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell><span className="text-sm font-medium">{a.visit_days || 0} {t('days', 'dias')}</span></TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={13} className="text-center text-slate-500 py-8">
                  {t('No users found', 'No se encontraron usuarios')}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
